import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';

const Backdrop = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const CartModal = styled(motion.div)`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(15px);
  border-radius: 20px;
  padding: 30px;
  max-width: 600px;
  width: 100%;
  max-height: 80vh;
  overflow-y: auto;
  color: #2c3e50;
  box-shadow: 0 25px 80px rgba(0, 0, 0, 0.4);
  border: 2px solid rgba(255, 255, 255, 0.8);
  position: relative;

  @media (max-width: 768px) {
    padding: 20px;
    max-width: 350px;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 15px;
  right: 15px;
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #7f8c8d;
  transition: color 0.3s ease;
  
  &:hover {
    color: #e74c3c;
  }
`;

const Title = styled.h2`
  font-size: 1.8rem;
  margin-bottom: 20px;
  text-align: center;
  background: linear-gradient(45deg, #ff6b6b, #4ecdc4);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-weight: 800;
`;

const EmptyCart = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #7f8c8d;
  
  h3 {
    margin-bottom: 10px;
    color: #95a5a6;
  }
  
  p {
    margin-bottom: 20px;
  }
`;

const CartItems = styled.div`
  margin-bottom: 20px;
`;

const CartItem = styled(motion.div)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px;
  background: rgba(255, 255, 255, 0.5);
  border-radius: 10px;
  margin-bottom: 10px;
  
  @media (max-width: 480px) {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
  }
`;

const ItemDetails = styled.div`
  flex: 1;
`;

const ItemName = styled.h4`
  margin: 0 0 5px 0;
  color: #2c3e50;
  font-weight: 600;
`;

const ItemDescription = styled.p`
  margin: 0;
  color: #7f8c8d;
  font-size: 0.9rem;
`;

const ItemPrice = styled.div`
  font-weight: bold;
  color: #2c3e50;
  font-size: 1.1rem;
`;

const QuantityControls = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0 15px;
  
  @media (max-width: 480px) {
    justify-content: center;
    margin: 10px 0;
  }
`;

const QuantityButton = styled.button`
  width: 30px;
  height: 30px;
  border: none;
  background: #4ecdc4;
  color: white;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  
  &:hover {
    background: #45b7b8;
  }
  
  &:disabled {
    background: #bdc3c7;
    cursor: not-allowed;
  }
`;

const QuantityDisplay = styled.span`
  font-weight: bold;
  min-width: 20px;
  text-align: center;
`;

const RemoveButton = styled.button`
  background: #e74c3c;
  color: white;
  border: none;
  padding: 5px 10px;
  border-radius: 5px;
  cursor: pointer;
  font-size: 0.8rem;
  
  &:hover {
    background: #c0392b;
  }
`;

const CartSummary = styled.div`
  border-top: 2px solid rgba(0, 0, 0, 0.1);
  padding-top: 20px;
  margin-top: 20px;
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
  
  &.total {
    font-size: 1.2rem;
    font-weight: bold;
    color: #2c3e50;
    border-top: 1px solid rgba(0, 0, 0, 0.1);
    padding-top: 10px;
    margin-top: 10px;
  }
`;

const CheckoutButton = styled(motion.button)`
  width: 100%;
  padding: 15px;
  background: linear-gradient(45deg, #4ecdc4, #45b7d1);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  margin-top: 15px;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(78, 205, 196, 0.3);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const ContinueShoppingButton = styled.button`
  width: 100%;
  padding: 12px;
  background: transparent;
  color: #4ecdc4;
  border: 2px solid #4ecdc4;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  margin-top: 10px;
  
  &:hover {
    background: rgba(78, 205, 196, 0.1);
  }
`;

const ShoppingCart = () => {
  const { user } = useAuth();
  const { 
    cartItems, 
    cartTotal, 
    cartItemCount,
    isCartOpen, 
    setIsCartOpen,
    removeFromCart,
    updateQuantity,
    clearCart,
    createCheckoutSession,
    loading 
  } = useCart();
  
  const [isProcessing, setIsProcessing] = useState(false);

  const handleQuantityChange = (itemId, newQuantity) => {
    updateQuantity(itemId, newQuantity);
  };

  const handleRemoveItem = (itemId) => {
    removeFromCart(itemId);
  };

  const handleCheckout = async () => {
    if (!user) {
      alert('Please log in to complete your purchase');
      return;
    }

    if (cartItems.length === 0) {
      return;
    }

    setIsProcessing(true);
    
    try {
      await createCheckoutSession();
    } catch (error) {
      console.error('Checkout error:', error);
      alert('Checkout failed. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleContinueShopping = () => {
    setIsCartOpen(false);
  };

  if (!isCartOpen) return null;

  return (
    <AnimatePresence>
      <Backdrop
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={() => setIsCartOpen(false)}
      >
        <CartModal
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          onClick={(e) => e.stopPropagation()}
        >
          <CloseButton onClick={() => setIsCartOpen(false)}>
            &times;
          </CloseButton>
          
          <Title>Shopping Cart</Title>
          
          {cartItems.length === 0 ? (
            <EmptyCart>
              <h3>🛒 Your cart is empty</h3>
              <p>Add some premium packages to get started!</p>
              <ContinueShoppingButton onClick={handleContinueShopping}>
                Browse Packages
              </ContinueShoppingButton>
            </EmptyCart>
          ) : (
            <>
              <CartItems>
                {cartItems.map((item) => (
                  <CartItem
                    key={item.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                  >
                    <ItemDetails>
                      <ItemName>{item.name}</ItemName>
                      <ItemDescription>{item.description}</ItemDescription>
                    </ItemDetails>
                    
                    {item.type !== 'subscription' && (
                      <QuantityControls>
                        <QuantityButton
                          onClick={() => handleQuantityChange(item.id, item.quantity - 1)}
                          disabled={item.quantity <= 1}
                        >
                          -
                        </QuantityButton>
                        <QuantityDisplay>{item.quantity}</QuantityDisplay>
                        <QuantityButton
                          onClick={() => handleQuantityChange(item.id, item.quantity + 1)}
                        >
                          +
                        </QuantityButton>
                      </QuantityControls>
                    )}
                    
                    <ItemPrice>
                      ${(item.price * item.quantity).toFixed(2)}
                      {item.interval && `/${item.interval}`}
                    </ItemPrice>
                    
                    <RemoveButton onClick={() => handleRemoveItem(item.id)}>
                      Remove
                    </RemoveButton>
                  </CartItem>
                ))}
              </CartItems>

              <CartSummary>
                <SummaryRow>
                  <span>Items ({cartItemCount}):</span>
                  <span>${cartTotal.toFixed(2)}</span>
                </SummaryRow>
                
                <SummaryRow className="total">
                  <span>Total:</span>
                  <span>${cartTotal.toFixed(2)}</span>
                </SummaryRow>
                
                <CheckoutButton
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleCheckout}
                  disabled={isProcessing || loading || cartItems.length === 0}
                >
                  {isProcessing ? (
                    'Processing...'
                  ) : (
                    `Checkout - $${cartTotal.toFixed(2)}`
                  )}
                </CheckoutButton>
                
                <ContinueShoppingButton onClick={handleContinueShopping}>
                  Continue Shopping
                </ContinueShoppingButton>
              </CartSummary>
            </>
          )}
        </CartModal>
      </Backdrop>
    </AnimatePresence>
  );
};

export default ShoppingCart; 